import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { normalizeError } from "../../../operationFeedback/normalizeError";
import { trainingApi, trainingRequestKey } from "../../../api/training";
import { missingTranslations, translatedLocales } from "./trainingTranslations";

export const inputClass =
  "min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:opacity-60";
export const buttonClass =
  "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50";
export const primaryClass = `${buttonClass} !border-transparent !bg-[#1463C7] !text-white hover:!bg-[#0B3A78]`;
export const panelClass =
  "rounded-2xl border border-slate-200 bg-white p-4 sm:p-6";
export const newKey = trainingRequestKey;
export const statusKeys = {
  draft: "trainingAdmin.draft",
  published: "trainingAdmin.active",
  deactivated: "trainingAdmin.inactive",
  archived: "trainingAdmin.archived",
  not_started: "trainingAdmin.notStarted",
  pending: "trainingAdmin.notStarted",
  in_progress: "trainingAdmin.inProgress",
  snoozed: "trainingAdmin.snoozed",
  completed: "trainingAdmin.completed",
  passed: "trainingAdmin.completed",
  exempt: "trainingAdmin.exempt",
  withdrawn: "trainingAdmin.withdrawn",
  overdue: "trainingAdmin.overdue",
  blocked_dependency: "trainingAdmin.blocked",
};
export const requirementKeys = {
  immediate: "trainingAdmin.immediate",
  deadline: "trainingAdmin.deadline",
  optional: "trainingAdmin.optional",
};
export function Badge({ status }) {
  const { t } = useTranslation();
  const color = ["published", "completed", "passed"].includes(status)
    ? "bg-emerald-50 text-emerald-800"
    : ["overdue", "blocked_dependency"].includes(status)
      ? "bg-rose-50 text-rose-800"
      : "bg-slate-100 text-slate-700";
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${color}`}
    >
      {t(statusKeys[status] || "trainingAdmin.notStarted")}
    </span>
  );
}
export function Field({ label, children, hint }) {
  return (
    <label className="block space-y-2 text-sm font-semibold text-slate-700">
      <span>{label}</span>
      {children}
      {hint && (
        <span className="block text-xs font-normal leading-5 text-slate-500">
          {hint}
        </span>
      )}
    </label>
  );
}
export function Feedback({ error, message, children }) {
  const { t } = useTranslation();
  if (!error && !message && React.Children.toArray(children).length === 0)
    return null;
  return (
    <div
      role={error ? "alert" : "status"}
      className={`rounded-xl border p-4 text-sm ${error ? "border-amber-200 bg-amber-50 text-amber-950" : "border-emerald-200 bg-emerald-50 text-emerald-950"}`}
    >
      {error ? (
        <>
          <p>
            {error.httpStatus === 409
              ? t("trainingAdmin.conflict")
              : error.httpStatus == null || error.httpStatus >= 500
                ? t("trainingAdmin.unknown")
                : error.safeMessage}
          </p>
          {error.fieldErrors?.length > 0 && (
            <ul className="mt-2 list-disc pl-5">
              {error.fieldErrors.map((item, index) => (
                <li key={index}>
                  {item.path !== "base" ? `${item.path}: ` : ""}
                  {item.message}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        message
      )}
      {children}
    </div>
  );
}

export function useAlive() {
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  return alive;
}

/** Remote, company-scoped options. Selected items remain visible while searching. */
export function RemoteOptions({
  kind,
  selected = [],
  onChange,
  multiple = true,
  label,
  disabled,
  renderLabel,
  onPick,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [known, setKnown] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [count, setCount] = useState(0);
  useEffect(() => {
    let current = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await trainingApi.options({
          kind,
          query,
          offset,
          limit: 30,
        });
        if (!current) return;
        const options = result.data || [];
        setRows(options);
        setCount(result.count || options.length);
        setError(null);
        setKnown((previous) => ({
          ...previous,
          ...Object.fromEntries(options.map((row) => [row.id, row])),
        }));
      } catch (e) {
        if (current) setError(normalizeError(e));
      } finally {
        if (current) setLoading(false);
      }
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [kind, query, offset]);
  const pick = (row) => {
    onPick?.(row);
    const next = multiple
      ? selected.includes(row.id)
        ? selected.filter((id) => id !== row.id)
        : [...selected, row.id]
      : [row.id];
    onChange?.(next);
  };
  return (
    <fieldset disabled={disabled} className="space-y-2">
      <legend className="mb-2 text-sm font-semibold text-slate-700">
        {label}
      </legend>
      <input
        className={inputClass}
        aria-label={label}
        type="search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOffset(0);
        }}
        placeholder={t(
          kind === "documents"
            ? "trainingAdmin.documentSearch"
            : "trainingAdmin.searchPeople",
        )}
      />
      {multiple && selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((id) => (
            <button
              key={id}
              type="button"
              className="rounded-full bg-blue-50 px-3 py-2 text-xs text-blue-800"
              onClick={() => onChange(selected.filter((value) => value !== id))}
            >
              {known[id]?.title || `#${id}`} ×
            </button>
          ))}
        </div>
      )}
      <Feedback error={error} />
      <div
        className="max-h-56 overflow-y-auto rounded-xl border border-slate-200"
        aria-busy={loading}
      >
        {!loading && !rows.length && (
          <p className="p-3 text-sm text-slate-500">
            {t(
              kind === "documents"
                ? "trainingAdmin.noDocuments"
                : "trainingAdmin.noResults",
            )}
          </p>
        )}
        {rows.map((row) => (
          <label
            key={row.id}
            className="flex min-h-[44px] cursor-pointer items-center gap-3 border-b border-slate-100 px-3 py-2 last:border-0 hover:bg-slate-50"
          >
            <input
              type={multiple ? "checkbox" : "radio"}
              checked={selected.includes(row.id)}
              onChange={() => pick(row)}
            />
            <span className="text-sm">
              {renderLabel ? renderLabel(row) : row.title}
            </span>
          </label>
        ))}
      </div>
      {count > 30 && (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className={buttonClass}
            disabled={loading || offset === 0}
            onClick={() => setOffset(Math.max(0, offset - 30))}
          >
            {t("trainingAdmin.previous")}
          </button>
          <button
            type="button"
            className={buttonClass}
            disabled={loading || offset + 30 >= count}
            onClick={() => setOffset(offset + 30)}
          >
            {t("trainingAdmin.next")}
          </button>
        </div>
      )}
    </fieldset>
  );
}

export const initialDraft = () => ({
  guide: {
    title: "",
    description: "",
    responsible_user_id: null,
    notification_settings: {
      enabled: false,
      mode: "daily",
      events: ["completed", "overdue", "blocked"],
    },
  },
  revision: {
    change_summary: "",
    content_locale: "nb",
    translations: {},
    pages: [{ key: newKey(), title: "", cards: [] }],
    policy: {
      audience: "all",
      position_ids: [],
      user_ids: [],
      requirement: "optional",
      mandatory_new_users_only: false,
      due_at: null,
      due_days: 7,
      estimated_minutes: 5,
      allow_snooze: true,
      allow_mandatory_snooze: false,
    },
  },
});
export const draftFromGuide = (guide) => {
  const defaults = initialDraft();
  const revision = JSON.parse(
    JSON.stringify(
      guide.draft_revision || guide.current_revision || defaults.revision,
    ),
  );
  revision.policy = { ...defaults.revision.policy, ...revision.policy };
  revision.content_locale = revision.content_locale || "nb";
  revision.translations = revision.translations || {};
  revision.pages = (revision.pages || defaults.revision.pages).map((page) => ({
    ...page,
    cards: (page.cards || []).map((card) => ({
      options: [],
      correct_option_ids: [],
      depends_on: [],
      body: "",
      title: "",
      image_alt: "",
      explanation: "",
      required: true,
      ...card,
    })),
  }));
  return {
    guide: {
      ...defaults.guide,
      ...guide,
      notification_settings: {
        ...defaults.guide.notification_settings,
        ...guide.notification_settings,
      },
    },
    revision,
  };
};
export const newCard = (type = "content") => ({
  key: newKey(),
  type,
  title: "",
  body: "",
  required: true,
  image_document_id: null,
  image_alt: "",
  options: ["", ""].map((text) => ({ id: newKey(), text })),
  correct_option_ids: [],
  explanation: "",
  document_version_id: null,
  document_required: type === "document",
  depends_on: [],
});
export function validateDraft({ guide, revision }) {
  const errors = [];
  if (!guide.title?.trim()) errors.push("trainingAdmin.validationTitle");
  const cards = revision.pages.flatMap((page) => page.cards);
  if (!cards.length) errors.push("trainingAdmin.validationCards");
  if (
    cards.some(
      (card) =>
        ["single_choice", "multiple_choice"].includes(card.type) &&
        (card.options.length < 2 ||
          card.options.some((option) => !option.text.trim()) ||
          !card.correct_option_ids.length ||
          (card.type === "single_choice" &&
            card.correct_option_ids.length !== 1) ||
          card.correct_option_ids.some(
            (id) => !card.options.some((option) => option.id === id),
          )),
    )
  )
    errors.push("trainingAdmin.validationAnswer");
  if (
    cards.some(
      (card) =>
        (card.type === "document" || card.document_required) &&
        !card.document_version_id,
    )
  )
    errors.push("trainingAdmin.validationDocument");
  if (cards.some((card) => card.image_document_id && !card.image_alt?.trim()))
    errors.push("trainingAdmin.validationImage");
  if (
    (revision.policy.audience === "positions" &&
      !revision.policy.position_ids?.length) ||
    (revision.policy.audience === "users" && !revision.policy.user_ids?.length)
  )
    errors.push("trainingAdmin.validationAudience");
  // Mirrors the API: a translated language must be complete before publication.
  if (
    translatedLocales(revision).some(
      (code) => missingTranslations({ guide, revision }, revision.translations[code]).length,
    )
  )
    errors.push("trainingAdmin.validationTranslations");
  return errors;
}

export function guidePayload(draft) {
  const { title, description, responsible_user_id, notification_settings } =
    draft.guide;
  const { pages, policy, change_summary, content_locale, translations } =
    draft.revision;
  return {
    guide: { title, description, responsible_user_id, notification_settings },
    revision: { pages, policy, change_summary, content_locale, translations },
    expected_version: draft.guide.lock_version,
  };
}
export function formatDate(value, locale) {
  return value
    ? new Date(value).toLocaleString(locale || "nb-NO", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—";
}
